/**
 * Metadata store — React/Zustand mirror of the Vue ERP store function
 * `getMetaDataDetails()` in `src/stores/encrypt-decrypt.ts:23-60`.
 *
 * Layering follows the Vue pattern exactly:
 *
 *   api/metadataApi.ts   → raw `response.data` (no unwrap)
 *   stores/metadata-store.ts (this file)
 *      → unwraps `body.data` (= `MetadataResponse`) and caches it in the
 *        SHARED `LocalCache.metadata` IndexedDB store. Both apps share
 *        the same cache row.
 *   features/.../page    → `await getMetaDataDetails()` once, then
 *                          `metadataMap[domainId]?.attributeDetails`
 *
 * Cache format (matches Vue `permission.store.ts:64-79`):
 *   IDB database: `LocalCache`
 *   Object store: `metadata`
 *   Entry:        { id: 'data', value: <body.data> }
 *                  ── i.e. { id: 'data', value: { domainMetaDataDetails: { ... } } }
 *
 * Read order on cold start:
 *   1. In-memory Zustand cache (instant; survives within a session)
 *   2. `LocalCache.metadata` (populated by Vue OR by us on a previous run)
 *   3. Network: POST /v1/users/metadata/domain/search (bulk for all
 *      `REACT_DOMAIN_IDS`)
 *
 * On a network fetch, the new domains are MERGED into the existing
 * `LocalCache.metadata` blob so this app never overwrites the data Vue
 * cached for OTHER domains it owns.
 */
import { create } from 'zustand';

import { getAttributeMetaData } from '../api/metadataApi';
import { REACT_DOMAIN_IDS } from '../constants/domainIds';
import type { MetadataResponse } from '../types/metadata';
import { logger } from '../utils/logger';
import {
  VUE_CACHE_STORES,
  readVueCache,
  writeVueCache,
  clearVueCache,
} from '../utils/vueLocalCache';

const TTL_MS = 60 * 60 * 1000;

interface MetadataCacheEntry {
  data: MetadataResponse;
  fetchedAt: number;
}

interface MetadataStoreState {
  cache: MetadataCacheEntry | null;
  loading: boolean;
  error: string | null;
  inflight: Promise<MetadataResponse> | null;
  setCache: (entry: MetadataCacheEntry | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInflight: (p: Promise<MetadataResponse> | null) => void;
}

export const useMetadataStore = create<MetadataStoreState>((set) => ({
  cache: null,
  loading: false,
  error: null,
  inflight: null,
  setCache: (cache) => set({ cache }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setInflight: (inflight) => set({ inflight }),
}));

const EMPTY_RESPONSE: MetadataResponse = { domainMetaDataDetails: {} };

/** Returns the subset of `REACT_DOMAIN_IDS` that aren't present in the
 *  cached blob. Only when this returns `[]` is the cache safe to short-
 *  circuit on; otherwise we must fetch the missing ones (Vue ERP caches
 *  its own domain list, which doesn't necessarily cover ours). */
const missingDomainIds = (
  data: MetadataResponse | null | undefined,
): string[] => {
  const domains = data?.domainMetaDataDetails ?? {};
  return REACT_DOMAIN_IDS.filter((id) => !domains[id]);
};

/**
 * Direct equivalent of Vue's `getMetaDataDetails()` — async, callable from
 * anywhere, cached, returns the full `MetadataResponse` blob.
 *
 *   const meta = await getMetaDataDetails();
 *   const attrs = meta.domainMetaDataDetails[domainId]?.attributeDetails;
 *
 * `force=true` bypasses both in-memory and IDB caches, hits the network
 * for every required domain, and overwrites the caches with the response.
 * Used by the version-gated dependency step when `/v1/common/master/version`
 * reports `attributeMetaData` as changed.
 */
export async function getMetaDataDetails(
  force = false,
): Promise<MetadataResponse> {
  const state = useMetadataStore.getState();

  // Fast path: in-memory cache covers every domain we need.
  if (
    !force &&
    state.cache &&
    Date.now() - state.cache.fetchedAt < TTL_MS &&
    missingDomainIds(state.cache.data).length === 0
  ) {
    logger.info('[metadata-store] cache hit (in-memory)', {
      domains: Object.keys(state.cache.data.domainMetaDataDetails ?? {}),
    });
    return state.cache.data;
  }
  // Force-refresh callers must not join an in-flight cache-friendly read,
  // otherwise the stale promise resolves first and the caller never sees
  // the freshly-fetched payload.
  if (!force && state.inflight) {
    logger.info('[metadata-store] joining in-flight request');
    return state.inflight;
  }

  // CRITICAL: snapshot the setters into local refs so they survive any
  // store-state-replacement that happens between the call site and the
  // async body. `useMetadataStore.getState()` returns a frozen snapshot
  // and the *setters themselves* are stable, but readers of "state" must
  // not mistake the snapshot for live state — always read fresh.
  const setCache = state.setCache;
  const setLoading = state.setLoading;
  const setError = state.setError;
  const setInflight = state.setInflight;

  setLoading(true);
  setError(null);
  logger.info('[metadata-store] fetch start', { REACT_DOMAIN_IDS });

  const promise = (async (): Promise<MetadataResponse> => {
    try {
      // 1. SHARED disk cache — written by Vue OR by us on a previous run.
      //    Vue's own bulk-fetch list (`meta-data.api.ts:30-42`) doesn't
      //    include every domain React features rely on (e.g.
      //    `AR_CONTACT_DOMAIN_ID` is missing from Vue's list). We therefore
      //    only short-circuit on the cache when it covers ALL of
      //    `REACT_DOMAIN_IDS`. If any are missing we fall through to the
      //    network and fetch them.
      const liveBefore = useMetadataStore.getState();
      let existing: MetadataResponse | undefined;
      if (!liveBefore.cache) {
        existing = await readVueCache<MetadataResponse>(VUE_CACHE_STORES.metadata);
        logger.info('[metadata-store] LocalCache.metadata read', {
          present: Boolean(existing),
          domains: existing
            ? Object.keys(existing.domainMetaDataDetails ?? {})
            : [],
        });
        // A force-refresh caller still keeps the previous payload around
        // (so we can merge in untouched Vue-owned domains below), but
        // skips the short-circuit that would return cache without hitting
        // the network.
        if (!force && existing && missingDomainIds(existing).length === 0) {
          setCache({ data: existing, fetchedAt: Date.now() });
          return existing;
        }
      }

      // 2. Network: fetch only the domains the cache is missing. If the
      //    cache is empty we fall back to the full list. Either way we
      //    avoid the wasteful "re-fetch every domain" behaviour, and Vue's
      //    pre-cached entries for shared domains are reused.
      const baseline =
        existing ?? useMetadataStore.getState().cache?.data ?? EMPTY_RESPONSE;
      const missing = missingDomainIds(baseline);
      // On a force-refresh (server reported `attributeMetaData` version
      // changed), re-fetch every React-owned domain — the cached entries
      // for those domains are stale by definition. Vue-owned domains we
      // don't touch are still preserved through the merge step below.
      // On a force-refresh, re-fetch every React-owned domain. Otherwise
      // fetch only the missing domains — or every React domain when nothing
      // is missing (initial load).
      let toFetch: string[];
      if (force) {
        toFetch = [...REACT_DOMAIN_IDS];
      } else if (missing.length > 0) {
        toFetch = missing;
      } else {
        toFetch = [...REACT_DOMAIN_IDS];
      }
      logger.info('[metadata-store] network fetch', { toFetch });
      const body = await getAttributeMetaData(toFetch);
      const fetched = body?.data ?? EMPTY_RESPONSE;
      logger.info('[metadata-store] network response', {
        domainsReturned: Object.keys(fetched.domainMetaDataDetails ?? {}),
      });

      // 3. Merge with anything Vue might have cached for OTHER domains, so
      //    we never wipe entries owned by the Vue side of the app.
      const merged: MetadataResponse = {
        domainMetaDataDetails: {
          ...(baseline.domainMetaDataDetails ?? {}),
          ...(fetched.domainMetaDataDetails ?? {}),
        },
      };

      const stillMissing = missingDomainIds(merged);
      if (stillMissing.length > 0) {
        logger.warn(
          '[metadata-store] backend returned no metadata for domains:',
          stillMissing,
        );
      }

      setCache({ data: merged, fetchedAt: Date.now() });
      logger.info('[metadata-store] cache set', {
        domains: Object.keys(merged.domainMetaDataDetails ?? {}),
      });
      await writeVueCache(VUE_CACHE_STORES.metadata, merged);
      return merged;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logger.error('[metadata-store] fetch failed', { err });
      setError(message);
      return EMPTY_RESPONSE;
    } finally {
      setLoading(false);
      setInflight(null);
    }
  })();

  setInflight(promise);
  return promise;
}

/** Drops both the in-memory and shared LocalCache entries. */
export async function invalidateMetadataCache(): Promise<void> {
  useMetadataStore.setState({ cache: null, error: null });
  await clearVueCache(VUE_CACHE_STORES.metadata);
}
